import { useState } from 'react'
import { useNomenclature } from '@/hooks/useNomenclature'
import { extractApiError } from '@/lib/api'
import { Button, LoadingSpinner } from '@/components/ui'
import { AppIcon } from '@/components/ui/app-icon'
import { NomenclatureModal } from '@/components/features/nomenclature/NomenclatureModal'
import { Plus, Pencil, Trash2 } from 'lucide-react'
import type { NomenclatureItem } from '@/types'

export function NomenclaturePage() {
  const { items, isLoading, isFetching, error, refetch, remove } = useNomenclature()
  const [modalOpen, setModalOpen] = useState(false)
  const [editItem, setEditItem] = useState<NomenclatureItem | null>(null)

  const handleEdit = (item: NomenclatureItem) => {
    setEditItem(item)
    setModalOpen(true)
  }

  const handleDelete = (gtin: string) => {
    if (window.confirm('Удалить товар?')) {
      remove(gtin)
    }
  }

  if (isLoading) return <LoadingSpinner />

  return (
    <div>
      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          Не удалось загрузить номенклатуру: {extractApiError(error)}
          <button type="button" onClick={() => refetch()} className="ml-3 underline">
            Повторить
          </button>
        </div>
      )}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          Номенклатура
          {!error && !isFetching && items.length > 0 && (
            <span className="ml-2 text-base font-normal text-gray-500">({items.length})</span>
          )}
        </h1>
        <Button onClick={() => { setEditItem(null); setModalOpen(true) }}>
          <Plus className="h-4 w-4" /> Добавить товар
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-3 font-medium text-gray-600">GTIN</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Название</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Бренд</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Артикул</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Действия</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-12 text-center text-gray-400">
                  Нет товаров. Нажмите «Добавить товар».
                </td>
              </tr>
            ) : items.map((item) => (
              <tr key={item.gtin} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                <td className="px-4 py-3 font-mono text-xs">{item.gtin}</td>
                <td className="px-4 py-3 font-medium">
                  <span>{item.name}</span>
                  {(item.km_template || item.ka_box_template || item.ka_pallet_template) && (
                    <span
                      className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-blue-100 text-blue-700"
                      title={[
                        item.km_template ? `КМ: ${item.km_template}` : '',
                        item.ka_box_template ? `КА коробки: ${item.ka_box_template}` : '',
                        item.ka_pallet_template ? `КА паллеты: ${item.ka_pallet_template}` : '',
                      ].filter(Boolean).join('\n')}
                    >
                      <AppIcon name="labels" className="h-3.5 w-3.5" />
                    </span>
                  )}
                  {(() => {
                    const stdKeys = new Set(['gtin','name','brand','product_code','description','km_template','ka_box_template','ka_pallet_template','created_at','updated_at','unit'])
                    const extraCount = Object.keys(item).filter(k => !stdKeys.has(k)).length
                    return extraCount > 0 ? (
                      <span
                        className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-purple-100 text-purple-700"
                        title={Object.entries(item).filter(([k]) => !stdKeys.has(k)).map(([k, v]) => `${k}: ${v}`).join('\n')}
                      >
                        +{extraCount} поля
                      </span>
                    ) : null
                  })()}
                </td>
                <td className="px-4 py-3 text-gray-600">{item.brand || '—'}</td>
                <td className="px-4 py-3 text-gray-600">{item.product_code || '—'}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      title="Редактировать"
                      onClick={() => handleEdit(item)}
                      className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-blue-600 transition-colors"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      title="Удалить"
                      onClick={() => handleDelete(item.gtin)}
                      className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-red-600 transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <NomenclatureModal
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); setEditItem(null) }}
        editItem={editItem}
      />
    </div>
  )
}
